import { useEffect, useState } from "react";
import { Download, Loader2, Search } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "—";

const DIRECTORY_FIELDS = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "registered_at", label: "Registered" },
  { key: "last_login_at", label: "Last sign-in" },
  { key: "status", label: "Account status" },
  { key: "newsletter_consent", label: "Newsletter consent" },
  { key: "reminders_consent", label: "Reminder consent" },
  { key: "assessment_count", label: "Completed discoveries" },
  { key: "last_assessment_at", label: "Last discovery" },
];

const ExportPanel = () => {
  const { member: me } = useAuth();
  const perms = me?.permissions || [];
  const can = (p) => perms.includes("*") || perms.includes(p);
  const [fields, setFields] = useState(["name", "email", "registered_at", "newsletter_consent"]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [newsletter, setNewsletter] = useState("");
  const [completed, setCompleted] = useState("");
  const [status, setStatus] = useState("");

  const toggleField = (f) =>
    setFields((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));

  const base = process.env.REACT_APP_BACKEND_URL;
  const directoryUrl = `${base}/api/admin/export/directory.csv?fields=${fields.join(",")}&registered_from=${from}&registered_to=${to}&newsletter=${newsletter}&completed=${completed}&status=${status}`;
  const marketingUrl = `${base}/api/admin/export/marketing.csv?registered_from=${from}&registered_to=${to}`;

  return (
    <div
      data-testid="export-builder"
      className="mt-8 rounded-[1.75rem] border border-forest/10 bg-white/75 p-6"
    >
      <h2 className="font-serif text-xl text-forest">Export contacts</h2>
      <p className="mt-1 text-xs leading-relaxed text-taupe">
        Downloads are generated on demand, never stored at a public link, and every export is
        recorded in the audit log with the fields and record count.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-taupe">
        <label className="flex items-center gap-2">
          From
          <input
            data-testid="export-from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="rounded-full border border-forest/20 bg-white/70 px-4 py-2 text-xs text-charcoal outline-none focus:border-gold"
          />
        </label>
        <label className="flex items-center gap-2">
          To
          <input
            data-testid="export-to"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="rounded-full border border-forest/20 bg-white/70 px-4 py-2 text-xs text-charcoal outline-none focus:border-gold"
          />
        </label>
        {can("export_directory") && (
          <>
            <select
              data-testid="export-newsletter-filter"
              value={newsletter}
              onChange={(e) => setNewsletter(e.target.value)}
              className="rounded-full border border-forest/20 bg-white/70 px-4 py-2 text-xs text-charcoal outline-none focus:border-gold"
            >
              <option value="">Any newsletter status</option>
              <option value="true">Newsletter consented</option>
              <option value="false">Not consented</option>
            </select>
            <select
              data-testid="export-completed-filter"
              value={completed}
              onChange={(e) => setCompleted(e.target.value)}
              className="rounded-full border border-forest/20 bg-white/70 px-4 py-2 text-xs text-charcoal outline-none focus:border-gold"
            >
              <option value="">Any discovery status</option>
              <option value="yes">Completed a discovery</option>
              <option value="no">No completed discovery</option>
            </select>
            <select
              data-testid="export-status-filter"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="rounded-full border border-forest/20 bg-white/70 px-4 py-2 text-xs text-charcoal outline-none focus:border-gold"
            >
              <option value="">Any account status</option>
              <option value="active">Active</option>
              <option value="deactivated">Deactivated</option>
            </select>
          </>
        )}
      </div>
      {can("export_directory") && (
        <div className="mt-4 flex flex-wrap gap-2">
          {DIRECTORY_FIELDS.map((f) => (
            <button
              key={f.key}
              data-testid={`export-field-${f.key}`}
              onClick={() => toggleField(f.key)}
              className={`rounded-full border px-3.5 py-1.5 text-[11px] font-semibold transition-colors ${
                fields.includes(f.key)
                  ? "border-forest bg-forest text-ivory"
                  : "border-forest/20 text-forest hover:border-gold"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}
      <div className="mt-5 flex flex-wrap gap-3">
        {can("export_directory") && (
          <a
            data-testid="export-directory-button"
            href={directoryUrl}
            className="inline-flex items-center gap-2 rounded-full bg-forest px-6 py-3 text-sm font-semibold text-ivory hover:bg-forest-deep"
          >
            <Download className="h-4 w-4" />
            Member Directory Export ({fields.length} fields)
          </a>
        )}
        {can("export_marketing") && (
          <a
            data-testid="export-marketing-button"
            href={marketingUrl}
            className="inline-flex items-center gap-2 rounded-full border border-forest/25 px-6 py-3 text-sm font-semibold text-forest hover:border-gold"
          >
            <Download className="h-4 w-4" />
            Marketing Subscribers Export
          </a>
        )}
      </div>
      <p className="mt-4 text-[11px] leading-relaxed text-taupe">
        The marketing export contains only people who explicitly consented to the newsletter.
        Neither export can include wellness classifications, questionnaire answers, family
        history, spiritual responses, or private notes. Those fields do not exist here.
      </p>
    </div>
  );
};

export default function AdminContacts() {
  const [subs, setSubs] = useState(null);
  const [members, setMembers] = useState(null);
  const [search, setSearch] = useState("");

  const load = (s = "") => {
    api.get(`/admin/subscribers?search=${encodeURIComponent(s)}`).then(({ data }) => setSubs(data));
    api.get(`/admin/members?search=${encodeURIComponent(s)}`).then(({ data }) => setMembers(data));
  };

  useEffect(() => {
    document.title = "Email & Contacts | Rooted Healing Admin";
    load("");
  }, []);

  return (
    <div data-testid="admin-contacts">
      <h1 className="font-serif text-3xl text-forest">Email & Contacts</h1>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-taupe">
        Contact records with consent status. Exports contain contact details only, never
        wellness answers or classifications. Assessment reminders and the newsletter are
        separate consents.
      </p>

      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-56">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-taupe" />
          <input
            data-testid="contacts-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(search)}
            placeholder="Search contacts"
            className="w-full rounded-full border border-forest/20 bg-white/70 py-3 pl-11 pr-5 text-sm outline-none focus:border-gold"
          />
        </div>
        <button
          data-testid="contacts-search-button"
          onClick={() => load(search)}
          className="rounded-full bg-forest px-6 py-3 text-sm font-semibold text-ivory hover:bg-forest-deep"
        >
          Search
        </button>
      </div>

      <ExportPanel />

      <div className="mt-8 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="rounded-[1.75rem] border border-forest/10 bg-white/75 p-6">
          <h2 className="font-serif text-xl text-forest">Newsletter subscribers</h2>
          <p className="mt-1 text-xs text-taupe">People who joined the letter directly.</p>
          {!subs ? (
            <Loader2 className="mt-6 h-6 w-6 animate-spin text-forest" />
          ) : (
            <div className="mt-4 flex max-h-96 flex-col gap-2 overflow-y-auto">
              {subs.subscribers.map((s) => (
                <div
                  key={s.email}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-forest/8 px-4 py-3 text-sm"
                >
                  <div>
                    <p className="font-semibold text-forest">{s.first_name}</p>
                    <p className="text-xs text-charcoal/70">{s.email}</p>
                  </div>
                  <div className="text-right text-xs text-taupe">
                    <p>via {s.source}</p>
                    <p>{fmt(s.created_at)}</p>
                  </div>
                </div>
              ))}
              {subs.subscribers.length === 0 && (
                <p className="py-6 text-center text-sm text-taupe">No subscribers match.</p>
              )}
            </div>
          )}
        </div>

        <div className="rounded-[1.75rem] border border-forest/10 bg-white/75 p-6">
          <h2 className="font-serif text-xl text-forest">Member contact records</h2>
          <p className="mt-1 text-xs text-taupe">
            Registration does not subscribe anyone to marketing. Consent columns below.
          </p>
          {!members ? (
            <Loader2 className="mt-6 h-6 w-6 animate-spin text-forest" />
          ) : (
            <div className="mt-4 flex max-h-96 flex-col gap-2 overflow-y-auto">
              {members.members.map((m) => (
                <div
                  key={m.user_id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-forest/8 px-4 py-3 text-sm"
                >
                  <div>
                    <p className="font-semibold text-forest">{m.name}</p>
                    <p className="text-xs text-charcoal/70">{m.email}</p>
                  </div>
                  <div className="text-right text-xs text-taupe">
                    <p>Newsletter: {m.consents?.newsletter ? "Yes" : "No"}</p>
                    <p>Reminders: {m.consents?.reminders ? "Yes" : "No"}</p>
                  </div>
                </div>
              ))}
              {members.members.length === 0 && (
                <p className="py-6 text-center text-sm text-taupe">No members match.</p>
              )}
            </div>
          )}
        </div>
      </div>

      <p className="mt-6 rounded-2xl bg-sage/60 px-5 py-4 text-xs leading-relaxed text-charcoal/70">
        Preparing for a future email service provider: the Integrations page holds provider
        credentials server-side, and this CSV export matches the format most providers import.
        Subscriber ownership stays with Rooted Healing at every step.
      </p>
    </div>
  );
}
